import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";

interface NavItem {
  label: string;
  icon: string;
  to: string;
}

const navItems: NavItem[] = [
  { label: "Dashboard", icon: "ri-dashboard-line", to: "/admin" },
  { label: "Proyectos", icon: "ri-folder-3-line", to: "/admin/proyectos" },
  { label: "Servicios", icon: "ri-stack-line", to: "/admin/servicios" },
  { label: "Tecnologías", icon: "ri-code-s-slash-line", to: "/admin/tecnologias" },
  { label: "Redes sociales", icon: "ri-share-line", to: "/admin/redes" },
  { label: "Contenido del sitio", icon: "ri-edit-line", to: "/admin/contenido" },
  { label: "Solicitudes", icon: "ri-mail-line", to: "/admin/solicitudes" },
  { label: "Media", icon: "ri-image-line", to: "/admin/media" },
  { label: "Identidad de marca", icon: "ri-palette-line", to: "/admin/marca" },
  { label: "Apariencia", icon: "ri-contrast-2-line", to: "/admin/apariencia" },
  { label: "SEO", icon: "ri-search-line", to: "/admin/seo" },
  { label: "Historial", icon: "ri-history-line", to: "/admin/historial" },
];

export default function AdminLayout() {
  const { user, signOut } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  async function handleSignOut() {
    await signOut();
    navigate("/admin/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-background-50 flex">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-foreground-950/30 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-white border-r border-background-200 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-background-200">
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-code-s-slash-line text-white text-lg" />
          </div>
          <div className="leading-tight">
            <p className="font-heading font-semibold text-sm text-foreground-950">MARK DIRECT</p>
            <p className="text-[11px] text-foreground-500">Panel de administración</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/admin"}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                }`
              }
            >
              <i className={`${item.icon} text-base w-5 h-5 flex items-center justify-center`} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-background-200">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors"
          >
            <i className="ri-logout-box-r-line text-base w-5 h-5 flex items-center justify-center" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-background-200 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100"
              aria-label="Abrir menú"
            >
              <i className="ri-menu-line text-xl text-foreground-700" />
            </button>
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-external-link-line text-base" />
              Ver sitio público
            </Link>
          </div>

          <div className="relative flex items-center gap-3">
            <div className="hidden sm:block text-right leading-tight">
              <p className="text-sm font-medium text-foreground-900">
                {user?.user_metadata?.name || "Administrador"}
              </p>
              <p className="text-xs text-foreground-500">{user?.email}</p>
            </div>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="w-9 h-9 rounded-full bg-primary-500 text-white flex items-center justify-center font-semibold text-sm cursor-pointer"
              aria-label="Menú de usuario"
            >
              {(user?.email?.charAt(0) || "A").toUpperCase()}
            </button>

            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-12 w-56 bg-white border border-background-200 rounded-lg z-20 py-1">
                  <div className="px-4 py-2 border-b border-background-100 sm:hidden">
                    <p className="text-sm font-medium text-foreground-900 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 whitespace-nowrap"
                  >
                    <i className="ri-logout-box-r-line text-base" />
                    Cerrar sesión
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}